import { type PractitionerSettings, practitionerIsComplete } from '@ergo/shared';
import { type FormEvent, useEffect, useState } from 'react';
import { api, errorMessage } from '../../api';
import { useResource } from '../../hooks';

/**
 * What the header of every facture says. The name and the address are
 * the mentions the arrêté 83-50/A makes mandatory — nothing can be issued while
 * they are empty. SIRET and ADELI are printed when filled: no text demands them,
 * the mutuelles ask for them (docs/juridique.md §1).
 *
 * Changing anything here only affects factures issued from now on: an issued one
 * carries its own frozen copy, and no edit ever reaches it.
 */
export function Praticienne() {
  const settings = useResource(() => api.settings.get(), []);
  const [form, setForm] = useState<PractitionerSettings>({
    name: '',
    title: '',
    address: '',
    phone: '',
    email: '',
    collaborationLine: '',
    paymentTerms: '',
    tariffLine: '',
    iban: '',
    bic: '',
    siret: '',
    adeli: '',
  });
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (settings.data) setForm(settings.data.practitioner);
  }, [settings.data]);

  const complete = practitionerIsComplete(form);

  function field(key: keyof PractitionerSettings) {
    return {
      value: form[key],
      onChange: (event: { target: { value: string } }) => {
        setForm((current) => ({ ...current, [key]: event.target.value }));
        setSaved(false);
      },
    };
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError(null);
    setSaved(false);
    try {
      await api.settings.update({
        practitionerName: form.name,
        practitionerTitle: form.title,
        invoiceAddress: form.address,
        invoiceTariffLine: form.tariffLine,
        practitionerPhone: form.phone,
        practitionerEmail: form.email,
        invoiceCollaborationLine: form.collaborationLine,
        invoicePaymentTerms: form.paymentTerms,
        iban: form.iban,
        bic: form.bic,
        siret: form.siret,
        adeli: form.adeli,
      });
      setSaved(true);
      settings.reload();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="panel">
      <h2 className="panel__title">Praticienne</h2>
      <p className="hint">
        L’en-tête des factures. Sans le nom, aucune facture ne peut être émise.
      </p>

      {settings.error && (
        <p className="error" role="alert">
          {settings.error}
        </p>
      )}

      <form className="form" onSubmit={onSubmit}>
        <label className="field">
          <span className="field__label">Nom</span>
          <input className="input" placeholder="Camille Dubois" required {...field('name')} />
        </label>

        <label className="field">
          <span className="field__label">Titre</span>
          <input className="input" placeholder="Ergothérapeute DE libérale" {...field('title')} />
          <span className="hint">Imprimé sous le nom, en tête de facture.</span>
        </label>

        <label className="field">
          <span className="field__label">Adresse sur les factures</span>
          <textarea
            className="input input--textarea"
            rows={2}
            placeholder="12 rue des Lilas, 31000 Toulouse"
            {...field('address')}
          />
          <span className="hint">
            Laisser vide pour ne pas imprimer d’adresse. Une facture doit normalement porter
            l’adresse professionnelle : à renseigner dès qu’elle en a une distincte du domicile.
          </span>
        </label>

        <div className="field-row">
          <label className="field">
            <span className="field__label">Téléphone</span>
            <input className="input" placeholder="06 12 34 56 78" {...field('phone')} />
          </label>

          <label className="field">
            <span className="field__label">Email affiché sur les factures</span>
            <input className="input" type="email" placeholder="camille@exemple.fr" {...field('email')} />
          </label>
        </div>
        <span className="hint">
          Imprimés sous l’adresse, chacun s’il est rempli. L’email est celui que les familles
          voient : ce n’est pas le compte d’envoi.
        </span>

        <label className="field">
          <span className="field__label">Ligne de collaboration</span>
          <input
            className="input"
            placeholder="Collaboratrice de Suzie François"
            {...field('collaborationLine')}
          />
          <span className="hint">Imprimée sous le reste de l’en-tête.</span>
        </label>

        <label className="field">
          <span className="field__label">Échéance de paiement</span>
          <input
            className="input"
            placeholder="30 jours à compter de la réalisation de la prestation."
            {...field('paymentTerms')}
          />
          <span className="hint">Le délai imprimé sous le décompte.</span>
        </label>

        <div className="field-row">
          <label className="field">
            <span className="field__label">IBAN</span>
            <input className="input" placeholder="FR76 4061 8803 8200 0408 7364 634" {...field('iban')} />
          </label>

          <label className="field">
            <span className="field__label">BIC</span>
            <input className="input" placeholder="BOUSFRPPXXX" {...field('bic')} />
          </label>
        </div>
        <span className="hint">
          Le « Mode de paiement » du pied de facture : imprimé dès que l’IBAN est rempli.
        </span>

        <label className="field">
          <span className="field__label">Mention tarifaire sur les factures</span>
          <textarea
            className="input input--textarea"
            rows={2}
            {...field('tariffLine')}
          />
          <span className="hint">Imprimée en bas de chaque facture.</span>
        </label>

        <div className="field-row">
          <label className="field">
            <span className="field__label">SIRET</span>
            <input className="input" placeholder="812 345 678 00019" {...field('siret')} />
          </label>

          <label className="field">
            <span className="field__label">ADELI</span>
            <input className="input" placeholder="319912345" {...field('adeli')} />
          </label>
        </div>
        <span className="hint">
          Facultatifs, mais les mutuelles les demandent : ils sont imprimés dès qu’ils sont remplis.
        </span>

        {!complete && settings.data && (
          <p className="hint hint--warning">
            Le nom manque : « Émettre la facture » restera indisponible dans le Rapport.
          </p>
        )}

        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}

        <div className="form__actions">
          <button className="button button--primary" type="submit" disabled={pending}>
            {pending ? 'Enregistrement…' : 'Enregistrer'}
          </button>
          {saved && <span className="muted">Enregistré.</span>}
        </div>
      </form>

      <div className="form__actions">
        <a className="button" href="/api/notes/exemple.pdf" target="_blank" rel="noopener">
          Générer une facture exemple
        </a>
        <span className="hint">Une fausse facture avec les réglages enregistrés, ouverte ici.</span>
      </div>
    </section>
  );
}
